import { useState, useCallback, useEffect } from 'react';
import apiClient, { ApiError } from '../services/apiClient';
import type { Matter, MatterWithClient, MatterFormData, UpdateMatterRequest } from '../types';

export const useMatters = (clientId?: string) => {
  const [matters, setMatters] = useState<Matter[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load matters for a specific client or all matters
  const loadMatters = useCallback(async (targetClientId?: string): Promise<void> => {
    setIsLoading(true);
    setError(null);

    try {
      let mattersData: Matter[];
      
      if (targetClientId || clientId) {
        // Load matters for specific client
        const id = targetClientId || clientId;
        mattersData = await apiClient.getClientMatters(id!);
      } else {
        // Load all matters
        mattersData = await apiClient.getMatters();
      }
      
      setMatters(mattersData);
    } catch (err) {
      const apiError = err as ApiError;
      setError(apiError.message);
      console.error('Failed to load matters:', apiError);
    } finally {
      setIsLoading(false);
    }
  }, [clientId]); // Removed isLoading from dependencies to prevent infinite loops

  // Create a new matter
  const createMatter = useCallback(async (targetClientId: string, matterData: MatterFormData): Promise<Matter | null> => {
    setIsLoading(true);
    setError(null);

    try {
      const newMatter = await apiClient.createMatter(targetClientId, matterData);
      
      // Update local state with optimistic update
      setMatters(prev => [newMatter, ...prev]);
      
      return newMatter;
    } catch (err) {
      const apiError = err as ApiError;
      setError(apiError.message);
      console.error('Failed to create matter:', apiError);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Update an existing matter
  const updateMatter = useCallback(async (
    targetClientId: string, 
    matterId: string, 
    matterData: UpdateMatterRequest
  ): Promise<Matter | null> => {
    setIsLoading(true);
    setError(null);

    try {
      const updatedMatter = await apiClient.updateMatter(targetClientId, matterId, matterData);
      
      // Update local state
      setMatters(prev => prev.map(matter => 
        matter.id === matterId ? updatedMatter : matter
      ));
      
      return updatedMatter;
    } catch (err) {
      const apiError = err as ApiError;
      setError(apiError.message);
      console.error('Failed to update matter:', apiError);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Delete a matter
  const deleteMatter = useCallback(async (targetClientId: string, matterId: string): Promise<boolean> => {
    setIsLoading(true);
    setError(null);

    try {
      await apiClient.deleteMatter(targetClientId, matterId);
      
      // Update local state with optimistic update
      setMatters(prev => prev.filter(matter => matter.id !== matterId));
      
      return true;
    } catch (err) {
      const apiError = err as ApiError;
      setError(apiError.message);
      console.error('Failed to delete matter:', apiError);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Get matter statistics for a client
  const getMatterStats = useCallback((clientMatters?: Matter[]) => {
    const targetMatters = clientMatters || matters;
    
    return {
      totalMatters: targetMatters.length,
      activeMatters: targetMatters.filter(m => m.status === 'active').length,
      completedMatters: targetMatters.filter(m => m.status === 'completed').length,
      onHoldMatters: targetMatters.filter(m => m.status === 'on-hold').length,
      cancelledMatters: targetMatters.filter(m => m.status === 'cancelled').length,
      totalBilled: targetMatters.reduce((sum, m) => sum + m.billedAmount, 0)
    };
  }, [matters]);

  // Auto-load matters when clientId changes, or load all matters if no clientId
  useEffect(() => {
    // Load matters on initial mount or when clientId changes
    loadMatters(clientId);
  }, [clientId, loadMatters]);

  return {
    matters,
    isLoading,
    error,
    loadMatters,
    createMatter,
    updateMatter,
    deleteMatter,
    getMatterStats,
    // Clear error manually
    clearError: () => setError(null)
  };
};

export default useMatters;

// Enhanced hook for getting matters with client information
export const useMattersWithClients = (clientId?: string) => {
  const [matters, setMatters] = useState<MatterWithClient[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load matters with client information for enhanced displays
  const loadMatters = useCallback(async (targetClientId?: string): Promise<void> => {
    setIsLoading(true);
    setError(null);

    try {
      let mattersData: MatterWithClient[];
      
      if (targetClientId || clientId) {
        // Load matters for specific client with client info
        const id = targetClientId || clientId;
        mattersData = await apiClient.getClientMattersWithClient(id!);
      } else {
        // Load all matters with client info
        mattersData = await apiClient.getMattersWithClients();
      }
      
      setMatters(mattersData);
    } catch (err) {
      const apiError = err as ApiError;
      setError(apiError.message);
      console.error('Failed to load matters with clients:', apiError);
    } finally {
      setIsLoading(false);
    }
  }, [clientId]);

  // Get matter statistics for a client
  const getMatterStats = useCallback((clientMatters?: MatterWithClient[]) => {
    const targetMatters = clientMatters || matters;
    
    return {
      totalMatters: targetMatters.length,
      activeMatters: targetMatters.filter(m => m.status === 'active').length,
      completedMatters: targetMatters.filter(m => m.status === 'completed').length,
      onHoldMatters: targetMatters.filter(m => m.status === 'on-hold').length,
      cancelledMatters: targetMatters.filter(m => m.status === 'cancelled').length,
      totalBilled: targetMatters.reduce((sum, m) => sum + m.billedAmount, 0)
    };
  }, [matters]);

  // Auto-load matters when clientId changes, or load all matters if no clientId
  useEffect(() => {
    loadMatters(clientId);
  }, [clientId, loadMatters]);

  return {
    matters,
    isLoading,
    error,
    loadMatters,
    getMatterStats,
    // Clear error manually
    clearError: () => setError(null)
  };
};
